import { analytics } from '@/lib/analytics';
import {
  audioAnalysisSchema,
  echoNestAudioAnalysisSchema,
  type AudioAnalysis,
  type EchoNestAudioAnalysis,
  type TempoTick,
} from '@/lib/audio-analysis/schema.gen';

export type ParsedAudioAnalysis = AudioAnalysis | EchoNestAudioAnalysis;

export function parseAudioAnalysis(payload: unknown): ParsedAudioAnalysis {
  const custom = audioAnalysisSchema.safeParse(payload);
  if (custom.success) return custom.data;
  const echo = echoNestAudioAnalysisSchema.safeParse(payload);
  if (echo.success) return echo.data;
  return payload as EchoNestAudioAnalysis;
}

export function isCustomAnalysis(data: ParsedAudioAnalysis): data is AudioAnalysis {
  return Array.isArray((data as AudioAnalysis).tempo_ticks);
}

export function tempoAtTime(
  ticks: TempoTick[] | undefined,
  time: number,
  source: TempoTick['source'] = 'dtempo',
): number | null {
  if (!ticks?.length) return null;
  const pool = ticks.filter((tick) => tick.source === source);
  if (!pool.length) return null;
  let best = pool[0];
  let bestDist = Math.abs(time - (best.start + best.duration / 2));
  for (const tick of pool) {
    const mid = tick.start + tick.duration / 2;
    const dist = Math.abs(time - mid);
    if (dist < bestDist) {
      best = tick;
      bestDist = dist;
    }
  }
  return best.tempo;
}

const TEMPO_RANGE_SPREAD = 8;

export type TempoFields = {
  tempo?: number;
  tempo_min?: number;
  tempo_max?: number;
};

export function tempoHasRange(track?: TempoFields | null): boolean {
  if (track?.tempo == null || track.tempo_min == null || track.tempo_max == null) {
    return false;
  }
  return track.tempo_max - track.tempo_min >= TEMPO_RANGE_SPREAD;
}

export function formatTempoPrimary(track?: TempoFields | null, digits = 0): string | null {
  if (track?.tempo == null) return null;
  return `${track.tempo.toFixed(digits)} BPM`;
}

export function formatTempoRange(track?: TempoFields | null): string | null {
  if (!tempoHasRange(track) || track?.tempo_min == null || track?.tempo_max == null) {
    return null;
  }
  return `${Math.round(track.tempo_min)}–${Math.round(track.tempo_max)}`;
}

export function formatTempoChip(track?: TempoFields | null): string | null {
  const primary = formatTempoPrimary(track, 0);
  if (!primary) return null;
  const range = formatTempoRange(track);
  return range ? `Tempo: ${Math.round(track!.tempo!)} BPM (${range})` : `Tempo: ${primary}`;
}

export type AnalysisJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export type AnalysisJob = {
  job_id: string;
  digest: string;
  filename: string | null;
  status: AnalysisJobStatus;
  stage: string | null;
  position: number;
  error?: string | null;
  coalesced?: number;
};

type AnalysisPaths = {
  submit: string;
  job: (id: string) => string;
  result: (id: string) => string;
  ws: (id: string) => string;
};

function analysisPaths(): AnalysisPaths {
  const base = import.meta.env.VITE_AUDIO_ANALYSIS_URL ?? 'http://localhost:8000';

  if (base.startsWith('/')) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return {
      submit: `${base}/analyze`,
      job: (id) => `${base}/analyze/jobs/${id}`,
      result: (id) => `${base}/analyze/jobs/${id}/result`,
      ws: (id) => `${proto}://${window.location.host}${base}/analyze/ws/${id}`,
    };
  }

  const url = new URL(base);
  const wsProto = url.protocol === 'https:' ? 'wss' : 'ws';
  return {
    submit: `${base.replace(/\/$/, '')}/audio-analysis`,
    job: (id) => `${base.replace(/\/$/, '')}/audio-analysis/jobs/${id}`,
    result: (id) => `${base.replace(/\/$/, '')}/audio-analysis/jobs/${id}/result`,
    ws: (id) => `${wsProto}://${url.host}/audio-analysis/ws/${id}`,
  };
}

export function describeAnalysisJob(job: AnalysisJob | null): string {
  if (!job) {
    return 'Analyzing…';
  }
  if (job.status === 'queued') {
    return job.position > 0 ? `Queued (#${job.position + 1})` : 'Queued';
  }
  if (job.status === 'running') {
    return job.stage ? `Analyzing: ${job.stage}` : 'Analyzing…';
  }
  if (job.status === 'failed') {
    return job.error || 'Analysis failed';
  }
  return 'Finishing…';
}

export type AudioAnalysisSource = 'upload' | 'spotify_match';

export type RunAudioAnalysisOptions = {
  onStatus?: (job: AnalysisJob) => void;
  signal?: AbortSignal;
  source?: AudioAnalysisSource;
};

function fileProps(file: File) {
  return {
    file_type: file.type || 'unknown',
    file_size_mb: Math.round((file.size / 1024 / 1024) * 100) / 100,
    file_name: file.name.slice(0, 120),
  };
}

function jobProps(job: AnalysisJob) {
  return {
    job_id: job.job_id,
    digest: job.digest.slice(0, 16),
    queue_position: job.position,
    coalesced: job.coalesced ?? 0,
    stage: job.stage,
  };
}

function errorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const record = payload as { error?: string; detail?: string };
    if (typeof record.error === 'string') return record.error;
    if (typeof record.detail === 'string') return record.detail;
  }
  return fallback;
}

export async function runAudioAnalysis(
  file: File,
  options: RunAudioAnalysisOptions = {},
): Promise<ParsedAudioAnalysis> {
  const { onStatus, signal, source = 'upload' } = options;
  const hasSpotifyMatch = source === 'spotify_match';
  const startedAt = Date.now();
  const base = { ...fileProps(file), source };
  analytics.audioAnalysisSubmitted(base);

  try {
    const paths = analysisPaths();
    const formData = new FormData();
    formData.append('file', file);

    const submitted = await fetch(paths.submit, { method: 'POST', body: formData, signal });
    if (submitted.status === 200) {
      const result = parseAudioAnalysis(await submitted.json());
      analytics.audioAnalysisCacheHit(base);
      analytics.audioAnalysisCompleted(result.track?.duration ?? 0, hasSpotifyMatch, {
        ...base,
        cache_hit: true,
        transport: 'cache',
        wait_ms: Date.now() - startedAt,
      });
      return result;
    }

    if (!submitted.ok) {
      const errorData = await submitted.json().catch(() => ({ error: 'Analysis failed' }));
      throw new Error(errorMessage(errorData, `HTTP ${submitted.status}: ${submitted.statusText}`));
    }

    const job = (await submitted.json()) as AnalysisJob;
    analytics.audioAnalysisQueued({ ...base, ...jobProps(job) });

    let started = false;
    const trackStatus = (next: AnalysisJob) => {
      if (!started && next.status === 'running') {
        started = true;
        analytics.audioAnalysisStarted({ ...base, ...jobProps(next) });
      }
      onStatus?.(next);
    };
    trackStatus(job);

    const { job: finished, transport } = await waitForJob(paths, job, trackStatus, signal);
    if (finished.status === 'failed') {
      throw new Error(finished.error || 'Analysis failed');
    }

    const resultResponse = await fetch(paths.result(finished.job_id), { signal });
    if (!resultResponse.ok) {
      const errorData = await resultResponse.json().catch(() => ({ error: 'Failed to load analysis' }));
      throw new Error(errorMessage(errorData, `HTTP ${resultResponse.status}: ${resultResponse.statusText}`));
    }
    const result = parseAudioAnalysis(await resultResponse.json());
    analytics.audioAnalysisCompleted(result.track?.duration ?? 0, hasSpotifyMatch, {
      ...base,
      ...jobProps(finished),
      cache_hit: false,
      transport,
      wait_ms: Date.now() - startedAt,
    });
    return result;
  } catch (err) {
    if (!(err instanceof DOMException && err.name === 'AbortError')) {
      const message = err instanceof Error ? err.message : 'Failed to analyze audio';
      analytics.audioAnalysisFailed(message, {
        ...base,
        wait_ms: Date.now() - startedAt,
      });
    }
    throw err;
  }
}

async function waitForJob(
  paths: AnalysisPaths,
  initial: AnalysisJob,
  onStatus: ((job: AnalysisJob) => void) | undefined,
  signal?: AbortSignal,
): Promise<{ job: AnalysisJob; transport: 'websocket' | 'poll' }> {
  const fromSocket = await waitViaSocket(paths.ws(initial.job_id), onStatus, signal);
  if (fromSocket) {
    return { job: fromSocket, transport: 'websocket' };
  }
  return { job: await waitViaPoll(paths.job(initial.job_id), onStatus, signal), transport: 'poll' };
}

function waitViaSocket(
  url: string,
  onStatus: ((job: AnalysisJob) => void) | undefined,
  signal?: AbortSignal,
): Promise<AnalysisJob | null> {
  return new Promise((resolve) => {
    let settled = false;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      resolve(null);
      return;
    }

    const finish = (job: AnalysisJob | null) => {
      if (settled) {
        return;
      }
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      window.clearTimeout(timer);
      try {
        socket.close();
      } catch {
        // already closed
      }
      resolve(job);
    };

    const onAbort = () => finish(null);
    signal?.addEventListener('abort', onAbort, { once: true });

    const timer = window.setTimeout(() => finish(null), 4000);

    socket.addEventListener('open', () => {
      window.clearTimeout(timer);
    });
    socket.addEventListener('error', () => finish(null));
    socket.addEventListener('close', () => {
      if (!settled) {
        finish(null);
      }
    });
    socket.addEventListener('message', (event) => {
      try {
        const job = JSON.parse(event.data) as AnalysisJob;
        if (!job?.status) {
          return;
        }
        onStatus?.(job);
        if (job.status === 'completed' || job.status === 'failed') {
          finish(job);
        } else if (!['queued', 'running'].includes(job.status)) {
          finish(null);
        }
      } catch {
        finish(null);
      }
    });
  });
}

async function waitViaPoll(
  url: string,
  onStatus: ((job: AnalysisJob) => void) | undefined,
  signal?: AbortSignal,
): Promise<AnalysisJob> {
  const deadline = Date.now() + 5 * 60 * 1000;
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException('Analysis aborted', 'AbortError');
    }
    const response = await fetch(url, { signal });
    if (response.status === 404) {
      throw new Error('Analysis job expired. Please try again.');
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const job = (await response.json()) as AnalysisJob;
    onStatus?.(job);
    if (job.status === 'completed' || job.status === 'failed') {
      return job;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1000));
  }
  throw new Error('Analysis timed out');
}
